import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { KnowledgeBaseCategory, OnboardingData } from '../types/knowledgeBase';
import axios from 'axios';
import { deleteDocumentKnowledge, getPdfViewUrl, getPdfDownloadUrl } from '../utils/documentKnowledgeAPI';

// TruncatedText component for privacy and condensed display
const TruncatedText = ({ text, maxLength = 150 }: { text: string, maxLength?: number }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text || text.length <= maxLength) return <p className="text-gray-300 text-sm">{text}</p>;

  return (
    <div className="relative">
      <div className={`overflow-hidden ${!isExpanded ? 'max-h-[80px]' : ''}`}>
        <p className="text-gray-300 text-sm whitespace-pre-line">
          {text}
        </p>
        {!isExpanded && (
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-primary/90 to-transparent pointer-events-none"></div>
        )}
      </div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-accent text-xs mt-1.5 hover:underline flex items-center"
      >
        {isExpanded ? (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 mr-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />
            </svg>
            <span className="text-blue-400">Show less</span>
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 mr-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
            <span className="bg-gradient-to-r from-blue-400 to-blue-500 bg-clip-text text-transparent font-medium">View more</span>
          </>
        )}
      </button>
    </div>
  );
};

// Define KnowledgeBaseItem type locally since we're adding numpages property
interface KnowledgeBaseItem {
  id: string;
  title: string;
  description: string;
  fileType: string;
  fileName: string;
  fileSize: number;
  dateAdded: string;
  lastAccessed?: string;
  tags: string[];
  category: KnowledgeBaseCategory;
  isLink?: boolean;
  linkUrl?: string;
  numpages?: number;
}

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [knowledgeBaseItems, setKnowledgeBaseItems] = useState<KnowledgeBaseItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<KnowledgeBaseItem | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedItem, setEditedItem] = useState<KnowledgeBaseItem | null>(null);
  const [activeCategory, setActiveCategory] = useState<KnowledgeBaseCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [isAddingDocLink, setIsAddingDocLink] = useState(false);
  const [newLinkData, setNewLinkData] = useState({
    title: '',
    url: '',
    description: '',
    category: 'company' as KnowledgeBaseCategory,
    tags: ''
  });
  const [newDocLinkData, setNewDocLinkData] = useState({
    docLink: '',
    description: '',
    category: 'company' as KnowledgeBaseCategory,
    tags: ''
  });
  const [isImportingGoogleDoc, setIsImportingGoogleDoc] = useState(false);

  // PDF loading state
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isPdfUrlError, setIsPdfUrlError] = useState(false);

  // Document upload dialog state
  const [isAddingDocument, setIsAddingDocument] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [newDocumentData, setNewDocumentData] = useState({
    title: '',
    description: '',
    category: 'company' as KnowledgeBaseCategory,
    tags: ''
  });

  // Onboarding data state
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
  const [isEditingOnboarding, setIsEditingOnboarding] = useState(false);
  const [editedOnboardingData, setEditedOnboardingData] = useState<OnboardingData | null>(null);
  const [newOnboardingLink, setNewOnboardingLink] = useState({title: '', url: ''});
  const [isSavingOnboarding, setIsSavingOnboarding] = useState(false);
  const [toast, setToast] = useState<{
    visible: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    visible: false,
    message: '',
    type: 'success'
  });
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<KnowledgeBaseItem | null>(null);

  // Extract the document fetching logic into a separate function
  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No JWT token found in localStorage');
        showToast('Authentication error. Please log in again.', 'error');
        return;
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const serverType = process.env.REACT_APP_SERVER_TYPE;
      const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

      // Fetch document knowledge
      const documentRoute = 'agent_gg_aukik/get?route=document_knowledge';
      const documentResponse = await axios.get(
        `${baseUrl}/${webhookPath}/${documentRoute}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (documentResponse.data) {
        // Handle response as an array of documents
        const documentsArray = Array.isArray(documentResponse.data)
          ? documentResponse.data
          : [documentResponse.data];

        // Convert API response items to KnowledgeBaseItem format
        const knowledgeItems = documentsArray.map((documentData: any) => {
          // Check if body is a string representation of JSON and parse it
          let body = documentData.body;
          if (typeof body === 'string' && body.trim().startsWith('{')) {
            try {
              body = JSON.parse(body);
            } catch (error) {
              console.error('Error parsing JSON body:', error);
              // Try to manually extract values from the string if JSON parsing fails
              try {
                // Create a simple object with extracted values
                const extractedBody: any = {};

                // Helper function to extract values between quotes
                const extractValue = (key: string) => {
                  const regex = new RegExp(`"${key}"\\s*:\\s*"([^"]*)"`, 'g');
                  const match = regex.exec(body as string);
                  return match?.[1] || '';
                };

                // Extract common fields
                extractedBody.title = extractValue('title');
                extractedBody.description = extractValue('description');
                extractedBody.category = extractValue('category');
                extractedBody.fileType = extractValue('fileType');
                extractedBody.fileName = extractValue('fileName');
                extractedBody.fileSize = extractValue('fileSize');
                extractedBody.dateAdded = extractValue('dateAdded');

                // Extract tags field with special handling
                try {
                  const tagsRegex = /"tags"\s*:\s*"(\[.*?\])"/;
                  const tagsMatch = tagsRegex.exec(body as string);
                  if (tagsMatch && tagsMatch[1]) {
                    // Clean up the extracted tags string to make it valid JSON
                    const tagsStr = tagsMatch[1].replace(/\\"/g, '"').replace(/\\\\/g, '\\');
                    extractedBody.tags = JSON.parse(tagsStr);
                  } else {
                    extractedBody.tags = ['document'];
                  }
                } catch (e) {
                  console.error('Error extracting tags:', e);
                  extractedBody.tags = ['document'];
                }

                body = extractedBody;
              } catch (extractError) {
                console.error('Error extracting values from body string:', extractError);
                // Continue with original string if extraction fails
              }
            }
          }

          // Parse tags - handling both string JSON representation and direct array
          let tags;
          try {
            if (body?.tags && typeof body.tags === 'string') {
              // Handle case where tags is a string like "[\"test\",\"doc\"]"
              // First, try to directly parse it
              try {
                tags = JSON.parse(body.tags);
              } catch (e) {
                // If direct parsing fails, try to clean the string and parse
                const cleanTagsStr = body.tags
                  .replace(/\\"/g, '"')           // Replace escaped quotes
                  .replace(/^"(.*)"$/, '$1')     // Remove surrounding quotes if present
                  .replace(/^'(.*)'$/, '$1')     // Remove surrounding single quotes if present
                  .replace(/\\n/g, '')          // Remove newlines
                  .replace(/\\t/g, '');         // Remove tabs

                try {
                  tags = JSON.parse(cleanTagsStr);
                } catch (e2) {
                  // If all parsing fails, use a default
                  tags = ['document'];
                }
              }
            } else if (Array.isArray(body?.tags)) {
              // Use existing tags if they're already in array format
              tags = body.tags;
            } else {
              tags = ['document']; // Default tags
            }
          } catch (error) {
            console.error('Error parsing tags:', error);
            tags = ['document']; // Fallback to default
          }

          return {
            id: documentData._id || `kb-${Date.now()}`,
            title: body?.title || documentData.info?.Title || 'Untitled Document',
            description: body?.description || 'No description provided',
            fileType: body?.fileType || 'pdf',
            fileName: body?.fileName || 'document.pdf',
            fileSize: parseInt(body?.fileSize) || 0,
            dateAdded: body?.dateAdded || new Date().toISOString(),
            tags,
            category: body?.category || 'company',
            numpages: documentData.numpages
          };
        });

        setKnowledgeBaseItems(knowledgeItems);
      } else {
        console.error('No document knowledge received from API');
        setKnowledgeBaseItems([]);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      showToast('Failed to load documents.', 'error');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.error('No JWT token found in localStorage');
          // Optionally show a toast or handle the error appropriately
          showToast('Authentication error. Please log in again.', 'error');
          setLoading(false);
          return;
        }

        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const serverType = process.env.REACT_APP_SERVER_TYPE;
        const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook'; // Assuming the same path structure applies

        // Fetch onboarding data
        const onboardingRoute = 'agent_gg_aukik/get?route=text_knowledge';
        const onboardingResponse = await axios.get<OnboardingData>(
          `${baseUrl}/${webhookPath}/${onboardingRoute}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (onboardingResponse.data) {
          // Add missing fields with dummy data if they don't exist in the API response
          const data = {
            ...onboardingResponse.data,
            // Add lastUpdated if missing
            lastUpdated: onboardingResponse.data.lastUpdated || new Date().toISOString(),
            // Add dummy integrations if missing
            integrations: onboardingResponse.data.integrations || ["Quickbooks", "ClickUp", "Stripe"],
            // Add dummy company profiles if missing
            companyProfiles: onboardingResponse.data.companyProfiles || [
              {
                id: "user-1",
                name: "Alex Chen",
                role: "Engineering Lead",
                email: "alex@company.com",
                department: "Engineering"
              },
              {
                id: "user-2",
                name: "Sam Rodriguez",
                role: "Product Manager",
                email: "sam@company.com",
                department: "Product"
              }
            ],
            // Add dummy links if missing
            links: onboardingResponse.data.links || [
              {
                title: "Company Google Drive (Coming Soon)",
                url: "https://drive.google.com/company-shared",
                category: "company",
                dateAdded: new Date().toISOString()
              },
              {
                title: "Team Figma Workspace (Coming Soon)",
                url: "https://figma.com/team/workspace",
                category: "team",
                dateAdded: new Date().toISOString()
              }
            ]
          };

          setOnboardingData(data);
        } else {
          // Handle case where data might be null or undefined in response
          console.error('No onboarding data received from API');
          setOnboardingData(null); // Or set to a default state
        }

        // Fetch documents
        await fetchDocuments();

      } catch (error) {
        console.error('Error fetching data:', error);
        showToast('Failed to load information.', 'error');
        setKnowledgeBaseItems([]);
        setOnboardingData(null); // Reset or handle error state
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); // Empty dependency array ensures this runs only once on mount

  const handleItemClick = (item: KnowledgeBaseItem) => {
    setSelectedItem(item);
    setEditedItem(item);
  };

  const handleClosePopup = () => {
    setSelectedItem(null);
    setIsEditing(false);
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleOnboardingInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof OnboardingData) => {
    if (!editedOnboardingData) return;

    setEditedOnboardingData({
      ...editedOnboardingData,
      [field]: e.target.value
    });
  };

  const handleAddOnboardingLink = () => {
    if (!editedOnboardingData || !newOnboardingLink.title || !newOnboardingLink.url) return;

    const newLink = {
      title: newOnboardingLink.title,
      url: newOnboardingLink.url,
      category: 'company',
      dateAdded: new Date().toISOString()
    };

    setEditedOnboardingData({
      ...editedOnboardingData,
      links: [...(editedOnboardingData.links || []), newLink]
    });

    setNewOnboardingLink({title: '', url: ''});
  };

  const handleRemoveOnboardingLink = (index: number) => {
    if (!editedOnboardingData || !editedOnboardingData.links) return;

    const updatedLinks = [...editedOnboardingData.links];
    updatedLinks.splice(index, 1);

    setEditedOnboardingData({
      ...editedOnboardingData,
      links: updatedLinks
    });
  };

  const postOnboardingData = async (data: OnboardingData) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No JWT token found in localStorage');
        return false;
      }

      const payload = {
        goals: data.goals,
        preferences: data.preferences,
        teamInformation: data.teamInformation || '',
        communicationStyle: data.communicationStyle || '',
        businessInformation: data.businessInformation || '',
        route: "add_text_knowledge"
      };

      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const serverType = process.env.REACT_APP_SERVER_TYPE;
      const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

      const response = await axios.post(
        `${baseUrl}/${webhookPath}/agent_gg_aukik/post`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Data successfully posted:', response.data);
      return true;
    } catch (error) {
      console.error('Error posting onboarding data:', error);
      return false;
    }
  };

  // Function to show toast notification
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({
      visible: true,
      message,
      type
    });

    // Auto-hide toast after 10 seconds
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 10000);
  };

  const handleSaveOnboardingChanges = async () => {
    if (!editedOnboardingData) return;

    setIsSavingOnboarding(true);

    try {
      // Post data to webhook
      const success = await postOnboardingData(editedOnboardingData);

      if (success) {
        // Update local state
        setOnboardingData({
          ...editedOnboardingData,
          lastUpdated: new Date().toISOString()
        });
        setIsEditingOnboarding(false);
        showToast('Onboarding information saved successfully!', 'success');
      } else {
        showToast('Failed to save changes. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error saving onboarding data:', error);
      showToast('An error occurred while saving your changes.', 'error');
    } finally {
      setIsSavingOnboarding(false);
    }
  };

  const handleCloseOnboardingEdit = () => {
    setIsEditingOnboarding(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof KnowledgeBaseItem) => {
    if (!editedItem) return;

    setEditedItem({
      ...editedItem,
      [field]: e.target.value
    });
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editedItem) return;

    setEditedItem({
      ...editedItem,
      tags: e.target.value.split(',').map(tag => tag.trim())
    });
  };

  const handleCategoryChange = (category: KnowledgeBaseCategory) => {
    if (!editedItem) return;

    setEditedItem({
      ...editedItem,
      category
    });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

      // Check if file type is supported
      if (!['pdf', 'docx', 'txt', 'csv', 'xlsx', 'xls'].includes(fileExtension)) {
        showToast('Unsupported file type. Please upload PDF, DOCX, TXT, CSV, XLSX, or XLS files.', 'error');
        return;
      }

      // Set the selected file and open document upload dialog
      setSelectedFile(file);
      setNewDocumentData({
        ...newDocumentData,
        title: file.name.split('.')[0]
      });
      setIsAddingDocument(true);
    }
  };

  const handleDocumentSubmit = async () => {
    if (!selectedFile || !newDocumentData.title.trim()) return;

    // Set uploading state to true
    setIsUploading(true);

    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase() || '';
    let fileType: 'pdf' | 'docx' | 'txt' | 'csv' | 'xlsx';
    if (fileExtension === 'pdf') fileType = 'pdf';
    else if (fileExtension === 'docx') fileType = 'docx';
    else if (fileExtension === 'csv') fileType = 'csv';
    else if (fileExtension === 'xlsx') fileType = 'xlsx';
    else fileType = 'txt';

    const newItem: KnowledgeBaseItem = {
      id: `kb-${Date.now()}`,
      title: newDocumentData.title,
      description: newDocumentData.description || 'Document uploaded by user',
      fileType,
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      dateAdded: new Date().toISOString(),
      tags: newDocumentData.tags ? newDocumentData.tags.split(',').map(tag => tag.trim()) : ['document'],
      category: newDocumentData.category
    };

    // Add to local state first for immediate UI update
    setKnowledgeBaseItems([...knowledgeBaseItems, newItem]);

    // Upload the document to the server
    const uploadSuccess = await uploadDocumentToServer(selectedFile, newItem);

    // If the upload was successful, refetch all documents to ensure we have the latest data
    if (uploadSuccess) {
      await fetchDocuments();
    }

    // Reset uploading state
    setIsUploading(false);

    // Close dialog and reset state
    setIsAddingDocument(false);
    setSelectedFile(null);
    setNewDocumentData({
      title: '',
      description: '',
      category: 'company',
      tags: ''
    });
  };

  const handleCancelDocumentAdd = () => {
    setIsAddingDocument(false);
    setSelectedFile(null);
    setNewDocumentData({
      title: '',
      description: '',
      category: 'company',
      tags: ''
    });
  };

  const uploadDocumentToServer = async (file: File, documentInfo: KnowledgeBaseItem) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No JWT token found in localStorage');
        showToast('Authentication error. Please log in again.', 'error');
        setIsUploading(false);
        return false;
      }

      // Create form data to send the file and metadata
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', documentInfo.title);
      formData.append('description', documentInfo.description);
      formData.append('category', documentInfo.category);
      formData.append('tags', JSON.stringify(documentInfo.tags));
      formData.append('fileType', documentInfo.fileType);
      formData.append('fileName', documentInfo.fileName);
      formData.append('fileSize', documentInfo.fileSize.toString());
      formData.append('dateAdded', documentInfo.dateAdded);
      formData.append('route', 'add_document_knowledge');

      // Show upload progress indicator
      showToast(`Uploading "${documentInfo.title}"...`, 'success');

      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const serverType = process.env.REACT_APP_SERVER_TYPE;
      const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

      const response = await axios.post(
        `${baseUrl}/${webhookPath}/agent_gg_aukik/post`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data) {
        showToast(`Document "${documentInfo.title}" uploaded successfully!`, 'success');
        console.log('Document upload response:', response.data);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error uploading document:', error);

      // Check if it's a 409 conflict error (file with same name exists)
      if (axios.isAxiosError(error) && error.response && error.response.status === 409 &&
          error.response.data && error.response.data.error === "File with the same name exists") {
        showToast(`Failed to upload document: File with the same name exists.`, 'error');
      } else {
        showToast(`Failed to upload document "${documentInfo.fileName}". Please try again.`, 'error');
      }

      // Optionally remove from local state if upload failed
      setKnowledgeBaseItems(prevItems =>
        prevItems.filter(item => item.id !== documentInfo.id)
      );

      return false;
    }
  };

  const handleAddNewItem = () => {
    // Reset the file input value to ensure the change event fires if the same file is selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const handleAddLink = () => {
    setIsAddingLink(true);
  };

  const handleAddDocLink = () => {
    setIsAddingDocLink(true);
  };

  const handleLinkSubmit = () => {
    if (!newLinkData.url.trim() || !newLinkData.title.trim()) return;

    const newItem: KnowledgeBaseItem = {
      id: `kb-link-${Date.now()}`,
      title: newLinkData.title,
      description: newLinkData.description || 'External resource link',
      fileType: 'link',
      fileName: newLinkData.title,
      fileSize: 0,
      dateAdded: new Date().toISOString(),
      tags: newLinkData.tags ? newLinkData.tags.split(',').map(tag => tag.trim()) : ['link'],
      category: newLinkData.category,
      isLink: true,
      linkUrl: newLinkData.url
    };

    setKnowledgeBaseItems([...knowledgeBaseItems, newItem]);
    setIsAddingLink(false);
    setNewLinkData({
      title: '',
      url: '',
      description: '',
      category: 'company',
      tags: ''
    });
  };

  const handleCancelLinkAdd = () => {
    setIsAddingLink(false);
    setNewLinkData({
      title: '',
      url: '',
      description: '',
      category: 'company',
      tags: ''
    });
  };

  const handleDocLinkSubmit = async () => {
    if (!newDocLinkData.docLink.trim()) return;

    try {
      setIsImportingGoogleDoc(true);

      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No JWT token found in localStorage');
        showToast('Authentication error. Please log in again.', 'error');
        setIsImportingGoogleDoc(false);
        return;
      }

      // Show upload progress indicator
      showToast(`Adding Google Doc link...`, 'success');

      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const serverType = process.env.REACT_APP_SERVER_TYPE;
      const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

      const requestData = {
        description: newDocLinkData.description || 'Google Doc imported by user',
        category: newDocLinkData.category,
        tags: newDocLinkData.tags ? newDocLinkData.tags.split(',').map(tag => tag.trim()) : ['document'],
        route: 'add_doc_link',
        docLink: newDocLinkData.docLink
      };

      const response = await axios.post(
        `${baseUrl}/${webhookPath}/agent_gg_aukik/post`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data) {
        showToast(`Google Doc imported successfully!`, 'success');
        console.log('Google Doc import response:', response.data);

        // Refetch documents to get the newly added document
        await fetchDocuments();

        // Reset and close dialog
        setIsAddingDocLink(false);
        setNewDocLinkData({
          docLink: '',
          description: '',
          category: 'company',
          tags: ''
        });
      }
    } catch (error) {
      console.error('Error importing Google Doc:', error);
      showToast(`Failed to import Google Doc. Please try again.`, 'error');
    } finally {
      setIsImportingGoogleDoc(false);
    }
  };

  const handleCancelDocLinkAdd = () => {
    setIsAddingDocLink(false);
    setNewDocLinkData({
      docLink: '',
      description: '',
      category: 'company',
      tags: ''
    });
  };

  const handleSaveChanges = () => {
    if (!editedItem) return;

    setKnowledgeBaseItems(knowledgeBaseItems.map(item =>
      item.id === editedItem.id ? editedItem : item
    ));
    setSelectedItem(editedItem);
    setIsEditing(false);
  };

  const handleDeleteButtonClick = () => {
    if (!selectedItem) return;
    setItemToDelete(selectedItem);
    setIsConfirmingDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    // Store reference to the item being deleted
    const deletingItem = itemToDelete;

    // Close the confirmation dialog
    setIsConfirmingDelete(false);

    // If it's a document from the server (has MongoDB _id)
    if (deletingItem.id && deletingItem.id.startsWith('kb-') === false) {
      try {
        // Show delete in progress toast
        showToast(`Deleting "${deletingItem.title}"...`, 'success');

        // Call the API to delete the document
        const success = await deleteDocumentKnowledge(deletingItem.id);

        if (success) {
          // If successful deletion from API, update UI
          setKnowledgeBaseItems(knowledgeBaseItems.filter(item => item.id !== deletingItem.id));
          // If the deleted item is the selected item, clear selection
          if (selectedItem && selectedItem.id === deletingItem.id) {
            setSelectedItem(null);
            setIsEditing(false);
          }
          showToast(`Document "${deletingItem.title}" deleted successfully!`, 'success');

          // Refetch documents after successful deletion
          await fetchDocuments();
        } else {
          // If API deletion failed
          showToast(`Failed to delete document "${deletingItem.title}". Please try again.`, 'error');
        }
      } catch (error) {
        console.error('Error deleting document:', error);
        showToast(`An error occurred while deleting the document. Please try again.`, 'error');
      }
    } else {
      // For local items or links (not stored on server)
      setKnowledgeBaseItems(knowledgeBaseItems.filter(item => item.id !== deletingItem.id));
      // If the deleted item is the selected item, clear selection
      if (selectedItem && selectedItem.id === deletingItem.id) {
        setSelectedItem(null);
        setIsEditing(false);
      }
    }

    // Clear the item to delete
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsConfirmingDelete(false);
    setItemToDelete(null);
  };

  // Change the existing handleDeleteItem function to call the new one
  const handleDeleteItem = handleDeleteButtonClick;

  // Function to view a PDF document
  const handleViewDocument = async (documentId: string) => {
    if (!documentId) return;

    setIsLoadingPdf(true);
    setIsPdfUrlError(false);

    try {
      const viewUrl = await getPdfViewUrl(documentId);

      if (viewUrl) {
        // Open the PDF in a new window/tab
        window.open(viewUrl, '_blank');
      } else {
        setIsPdfUrlError(true);
        showToast('Error retrieving document view URL.', 'error');
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      setIsPdfUrlError(true);
      showToast('Failed to view document. Please try again.', 'error');
    } finally {
      setIsLoadingPdf(false);
    }
  };

  // Function to download a PDF document
  const handleDownloadDocument = async (documentId: string) => {
    if (!documentId) return;

    setIsLoadingPdf(true);
    setIsPdfUrlError(false);

    try {
      const downloadUrl = await getPdfDownloadUrl(documentId);

      if (downloadUrl) {
        // Get the file from the selected knowledge base item
        const fileItem = knowledgeBaseItems.find(item => item.id === documentId);
        const fileName = fileItem?.fileName || `document-${documentId}.pdf`;

        // Fetch the binary file directly
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.error('No JWT token found in localStorage');
          showToast('Authentication error. Please log in again.', 'error');
          return;
        }

        const response = await axios.get(downloadUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          responseType: 'blob' // Important: we want a binary response
        });

        // Create a blob from the response data
        const blob = new Blob([response.data], { type: response.headers['content-type'] });

        // Create a download link and trigger the download
        const url = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.setAttribute('download', fileName);
        document.body.appendChild(downloadLink);
        downloadLink.click();

        // Clean up
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(url);
      } else {
        setIsPdfUrlError(true);
        showToast('Error retrieving document download URL.', 'error');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      setIsPdfUrlError(true);
      showToast('Failed to download document. Please try again.', 'error');
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredItems = knowledgeBaseItems.filter(item => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const matchesSearch = searchQuery === '' ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesCategory && matchesSearch;
  });

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'docx':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'csv':
      case 'xlsx':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'link':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  return (
    <div className="min-h-screen bg-secondary text-white">
      {/* Animation CSS */}
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }

          .card-hover {
            transition: all 0.3s ease;
          }

          .card-hover:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 20px -10px rgba(0, 0, 0, 0.3);
            border-color: rgba(99, 102, 241, 0.4);
          }

          .gradient-border {
            position: relative;
            background-clip: padding-box;
            border: 2px solid transparent;
          }

          .gradient-border::after {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            background: linear-gradient(45deg, #4f46e5, #8b5cf6, #ec4899);
            z-index: -1;
            border-radius: inherit;
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .card-hover:hover .gradient-border::after {
            opacity: 1;
          }
        `}
      </style>

      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div className="absolute -top-1/2 -left-1/4 w-3/4 h-3/4 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-1/2 -right-1/4 w-3/4 h-3/4 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 left-1/3 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <header className="bg-primary/80 backdrop-blur-md px-6 py-4 border-b border-border-color sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold text-white">Knowledge Base</h1>
          <button
            onClick={() => navigate('/chat')}
            className="text-sm px-4 py-2 rounded-md bg-accent hover:bg-accent/90 transition-colors shadow-lg shadow-accent/20"
          >
            Back to Chat
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 relative z-1">
        <div className="bg-primary/90 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl border border-white/5 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-accent/5 opacity-50"></div>

          <div className="p-8 relative">
            {/* Privacy Notice Banner */}
            <div className="mb-6 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
              </svg>
              <div>
                <p className="text-sm text-blue-100">For privacy, longer text is automatically truncated. Click "Expand" to view complete content when needed.</p>
              </div>
            </div>

            <div className="mb-6 pb-6 border-b border-border-color flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-extrabold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  Knowledge Base
                </h2>
                <p className="text-gray-400">Manage and search through your organization's knowledge resources</p>
              </div>
              <div className="flex items-center gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.docx,.txt,.csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                />
              </div>
            </div>

            {/* Onboarding Information Card */}
            {!loading && onboardingData && (
              <div className="mb-8 bg-gradient-to-r from-accent/10 to-purple-600/10 rounded-xl p-6 border border-accent/20">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-accent/20 p-2 rounded-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-accent">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white">Onboarding Information</h3>
                  </div>
                  <div>
                    <button
                      onClick={() => {
                        setIsEditingOnboarding(true);
                        setEditedOnboardingData(onboardingData);
                      }}
                      className="flex items-center gap-1 text-sm text-accent hover:text-white transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                      </svg>
                      Edit
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-accent/90 mb-2">Goals</h4>
                      <TruncatedText text={onboardingData.goals} />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-accent/90 mb-2">Preferences</h4>
                      <TruncatedText text={onboardingData.preferences} />
                    </div>
                    {onboardingData.teamInformation && (
                      <div>
                        <h4 className="text-sm font-medium text-accent/90 mb-2">Team Information</h4>
                        <TruncatedText text={onboardingData.teamInformation} />
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    {onboardingData.businessInformation && (
                      <div>
                        <h4 className="text-sm font-medium text-accent/90 mb-2">Business Information</h4>
                        <TruncatedText text={onboardingData.businessInformation} />
                      </div>
                    )}
                    {onboardingData.communicationStyle && (
                      <div>
                        <h4 className="text-sm font-medium text-accent/90 mb-2">Communication Style</h4>
                        <TruncatedText text={onboardingData.communicationStyle} />
                      </div>
                    )}
                    {onboardingData.integrations && onboardingData.integrations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-accent/90 mb-2">Integrations (Coming Soon)</h4>
                        <div className="flex flex-wrap gap-2">
                          {onboardingData.integrations.map((integration, index) => (
                            <span key={index} className="px-2 py-1 bg-gray-700 rounded-full text-xs text-gray-300">
                              {integration}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {onboardingData.companyProfiles && onboardingData.companyProfiles.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-accent/90 mb-2">Key Team Members (Coming Soon)</h4>
                        <div className="bg-black/20 rounded-lg p-4 max-h-32 overflow-y-auto">
                          {onboardingData.companyProfiles.map((profile) => (
                            <div key={profile.id} className="flex items-center gap-2 mb-2 last:mb-0">
                              <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center font-medium text-accent">
                                {profile.name.charAt(0)}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-300">{profile.name}</div>
                                <div className="text-xs text-gray-400">{profile.role}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 text-xs text-gray-500 text-right">
                  Last updated: {new Date(onboardingData.lastUpdated).toLocaleDateString()}
                </div>
              </div>
            )}

            {/* Document Library Section Header */}
            <div className="mb-6 relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="px-4 py-2 bg-gradient-to-r from-accent/10 to-purple-500/10 rounded-lg border border-accent/20 shadow-lg shadow-accent/5 relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-accent/5 to-purple-500/5 rounded-lg blur-md"></div>
                    <div className="relative flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-accent">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                      </svg>
                      <h3 className="text-lg font-bold bg-gradient-to-r from-white to-purple-300 bg-clip-text text-transparent">Document Library</h3>
                    </div>
                  </div>
                  <div className="h-px w-16 bg-gradient-to-r from-border-color to-transparent"></div>
                </div>

                <div className="flex gap-3">
                  {/* <button
                    onClick={handleAddLink}
                    className="flex items-center gap-2 px-4 py-2 bg-tertiary hover:bg-gray-700 rounded-lg transition-colors text-white text-sm font-medium"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-purple-400">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                    </svg>
                    Add Link (Coming Soon)
                  </button> */}
                  <button
                    onClick={handleAddDocLink}
                    className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 rounded-lg transition-colors text-white text-sm font-medium shadow-lg shadow-accent/20"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Google Doc Link
                  </button>
                  <button
                    onClick={handleAddNewItem}
                    className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 rounded-lg transition-colors text-white text-sm font-medium shadow-lg shadow-accent/20"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Upload PDF
                  </button>
                </div>
              </div>
              <div className="absolute -left-2 -top-2 w-2 h-2 bg-accent rounded-full animate-ping opacity-75"></div>
              <div className="absolute -right-2 -bottom-2 w-2 h-2 bg-purple-500 rounded-full animate-ping opacity-75 animation-delay-700"></div>
            </div>

            {/* Search and Filter */}
            <div className="mb-6 flex flex-col md:flex-row justify-between gap-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search documents, tags, or descriptions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full bg-tertiary border border-border-color rounded-lg py-2 pl-10 pr-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setActiveCategory('all')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === 'all'
                      ? 'bg-accent text-white'
                      : 'bg-tertiary hover:bg-tertiary text-gray-300'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setActiveCategory('company')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === 'company'
                      ? 'bg-accent text-white'
                      : 'bg-tertiary hover:bg-tertiary text-gray-300'
                  }`}
                >
                  Company
                </button>
                <button
                  onClick={() => setActiveCategory('team')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === 'team'
                      ? 'bg-accent text-white'
                      : 'bg-tertiary hover:bg-tertiary text-gray-300'
                  }`}
                >
                  Team
                </button>
                <button
                  onClick={() => setActiveCategory('project')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === 'project'
                      ? 'bg-accent text-white'
                      : 'bg-tertiary hover:bg-tertiary text-gray-300'
                  }`}
                >
                  Project
                </button>
                <button
                  onClick={() => setActiveCategory('personal')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === 'personal'
                      ? 'bg-accent text-white'
                      : 'bg-tertiary hover:bg-tertiary text-gray-300'
                  }`}
                >
                  Personal
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
              </div>
            ) : (
              <>
                {filteredItems.length === 0 ? (
                  <div className="text-center py-12">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-xl font-medium text-gray-300 mb-2">No documents found</h3>
                    <p className="text-gray-400">Try adjusting your search or filter criteria</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredItems.map(item => (
                      <div
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        className="card-hover bg-tertiary rounded-xl overflow-hidden border border-border-color cursor-pointer"
                      >
                        <div className="gradient-border p-6">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 bg-gray-700 rounded-lg p-3">
                              {getFileIcon(item.fileType)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg font-medium text-white truncate" title={item.title}>
                                {item.title}
                              </h3>
                              <p className="text-sm text-gray-400 mt-1 line-clamp-2" title={item.description}>
                                {item.description}
                              </p>
                            </div>
                          </div>

                          <div className="mt-4 flex flex-wrap gap-1.5">
                            {item.tags.map(tag => (
                              <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
                                {tag}
                              </span>
                            ))}
                          </div>

                          <div className="mt-4 pt-4 border-t border-border-color flex justify-between text-xs text-gray-400">
                            <div>
                              {formatFileSize(item.fileSize)}
                            </div>
                            <div>
                              Added: {formatDate(item.dateAdded)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Link Addition Popup */}
      <AnimatePresence>
        {isAddingLink && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-primary rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-border-color"
            >
              <div className="p-6 border-b border-border-color flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Add External Link (Coming Soon)</h2>
                <button
                  onClick={handleCancelLinkAdd}
                  className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Link Title</label>
                    <input
                      type="text"
                      value={newLinkData.title}
                      onChange={(e) => setNewLinkData({...newLinkData, title: e.target.value})}
                      placeholder="Enter a title for this link"
                      className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">URL</label>
                    <input
                      type="url"
                      value={newLinkData.url}
                      onChange={(e) => setNewLinkData({...newLinkData, url: e.target.value})}
                      placeholder="https://drive.google.com/company-shared or https://figma.com/team/workspace"
                      className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                    <textarea
                      value={newLinkData.description}
                      onChange={(e) => setNewLinkData({...newLinkData, description: e.target.value})}
                      placeholder="What is this link about?"
                      rows={3}
                      className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Category</label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['company', 'team', 'project', 'personal'] as KnowledgeBaseCategory[]).map(category => (
                        <div
                          key={category}
                          onClick={() => setNewLinkData({...newLinkData, category})}
                          className={`px-3 py-2 rounded-lg cursor-pointer text-center text-sm transition-colors ${
                            newLinkData.category === category
                              ? 'bg-accent/20 text-accent border border-accent/30'
                              : 'bg-tertiary hover:bg-gray-700'
                          }`}
                        >
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Tags (comma separated)</label>
                    <input
                      type="text"
                      value={newLinkData.tags}
                      onChange={(e) => setNewLinkData({...newLinkData, tags: e.target.value})}
                      placeholder="google, drive, important, etc."
                      className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-2">
                    <button
                      onClick={handleCancelLinkAdd}
                      className="px-4 py-2 bg-tertiary hover:bg-gray-700 transition-colors rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleLinkSubmit}
                      className="px-4 py-2 bg-accent hover:bg-accent/90 transition-colors rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      // disabled={!newLinkData.url.trim() || !newLinkData.title.trim()}
                      disabled={true}
                    >
                      Add Link (Coming Soon)
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Google Doc Link Addition Popup */}
      <AnimatePresence>
        {isAddingDocLink && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-primary rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-border-color"
            >
              <div className="p-6 border-b border-border-color flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Add Google Doc Link</h2>
                <button
                  onClick={handleCancelDocLinkAdd}
                  className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Google Doc URL</label>
                    <input
                      type="url"
                      value={newDocLinkData.docLink}
                      onChange={(e) => setNewDocLinkData({...newDocLinkData, docLink: e.target.value})}
                      placeholder="https://docs.google.com/document/d/..."
                      className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                    <textarea
                      value={newDocLinkData.description}
                      onChange={(e) => setNewDocLinkData({...newDocLinkData, description: e.target.value})}
                      placeholder="What is this Google Doc about?"
                      rows={3}
                      className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Category</label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['company', 'team', 'project', 'personal'] as KnowledgeBaseCategory[]).map(category => (
                        <div
                          key={category}
                          onClick={() => setNewDocLinkData({...newDocLinkData, category})}
                          className={`px-3 py-2 rounded-lg cursor-pointer text-center text-sm transition-colors ${
                            newDocLinkData.category === category
                              ? 'bg-accent/20 text-accent border border-accent/30'
                              : 'bg-tertiary hover:bg-gray-700'
                          }`}
                        >
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Tags (comma separated)</label>
                    <input
                      type="text"
                      value={newDocLinkData.tags}
                      onChange={(e) => setNewDocLinkData({...newDocLinkData, tags: e.target.value})}
                      placeholder="google, document, important, etc."
                      className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-2">
                    <button
                      onClick={handleCancelDocLinkAdd}
                      className="px-4 py-2 bg-tertiary hover:bg-gray-700 transition-colors rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDocLinkSubmit}
                      className="px-4 py-2 bg-accent hover:bg-accent/90 transition-colors rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!newDocLinkData.docLink.trim() || isImportingGoogleDoc}
                    >
                      {isImportingGoogleDoc ? (
                        <div className="flex items-center">
                          <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Importing...
                        </div>
                      ) : "Import Google Doc"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Document Upload Dialog */}
      <AnimatePresence>
        {isAddingDocument && selectedFile && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-primary rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-border-color"
            >
              <div className="p-6 border-b border-border-color flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Upload Document</h2>
                <button
                  onClick={handleCancelDocumentAdd}
                  className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4 p-3 bg-tertiary rounded-lg flex items-center">
                  <div className="bg-gray-700 rounded-lg p-2 mr-3">
                    {selectedFile.name.endsWith('.pdf') ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    ) : selectedFile.name.endsWith('.docx') ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    ) : selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.xlsx') ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium truncate">{selectedFile.name}</p>
                    <p className="text-gray-400 text-sm">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Document Title</label>
                    <input
                      type="text"
                      value={newDocumentData.title}
                      onChange={(e) => setNewDocumentData({...newDocumentData, title: e.target.value})}
                      placeholder="Enter a title for this document"
                      className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                    <textarea
                      value={newDocumentData.description}
                      onChange={(e) => setNewDocumentData({...newDocumentData, description: e.target.value})}
                      placeholder="What is this document about?"
                      rows={3}
                      className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Category</label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['company', 'team', 'project', 'personal'] as KnowledgeBaseCategory[]).map(category => (
                        <div
                          key={category}
                          onClick={() => setNewDocumentData({...newDocumentData, category})}
                          className={`px-3 py-2 rounded-lg cursor-pointer text-center text-sm transition-colors ${
                            newDocumentData.category === category
                              ? 'bg-accent/20 text-accent border border-accent/30'
                              : 'bg-tertiary hover:bg-gray-700'
                          }`}
                        >
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Tags (comma separated)</label>
                    <input
                      type="text"
                      value={newDocumentData.tags}
                      onChange={(e) => setNewDocumentData({...newDocumentData, tags: e.target.value})}
                      placeholder="important, report, finance, etc."
                      className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-2">
                    <button
                      onClick={handleCancelDocumentAdd}
                      className="px-4 py-2 bg-tertiary hover:bg-gray-700 transition-colors rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDocumentSubmit}
                      className="px-4 py-2 bg-accent hover:bg-accent/90 transition-colors rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!newDocumentData.title.trim() || isUploading}
                    >
                      {isUploading ? (
                        <div className="flex items-center">
                          <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Uploading...
                        </div>
                      ) : "Upload Document"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Document Detail Popup */}
      <AnimatePresence>
        {selectedItem && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-primary rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar border border-border-color"
            >
              <div className="sticky top-0 z-10 bg-primary p-6 border-b border-border-color flex justify-between items-center backdrop-blur-md bg-opacity-95">
                <h2 className="text-xl font-bold text-white">
                  {isEditing ? "Edit Document" : "Document Details"}
                </h2>
                <div className="flex gap-2">
                  {!isEditing && (
                    <>
                      <button
                        onClick={handleDeleteItem}
                        className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                      </button>
                      <button
                        className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-700/50 text-gray-400 cursor-not-allowed"
                        disabled
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                        </svg>
                        <span className="text-xs">Edit (Coming Soon)</span>
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleClosePopup}
                    className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {isEditing ? (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Document Title</label>
                      <input
                        type="text"
                        value={editedItem?.title || ''}
                        onChange={(e) => handleInputChange(e, 'title')}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                      <textarea
                        value={editedItem?.description || ''}
                        onChange={(e) => handleInputChange(e, 'description')}
                        rows={4}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                      />
                    </div>

                    {editedItem?.isLink ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">URL</label>
                        <input
                          type="url"
                          value={editedItem?.linkUrl || ''}
                          onChange={(e) => {
                            if (editedItem) {
                              setEditedItem({
                                ...editedItem,
                                linkUrl: e.target.value
                              });
                            }
                          }}
                          className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">File Name</label>
                        <div className="flex items-center px-4 py-2 bg-tertiary rounded-lg border border-border-color">
                          <span className="text-gray-300">{editedItem?.fileName}</span>
                          <span className="ml-2 text-xs text-gray-500">({formatFileSize(editedItem?.fileSize || 0)})</span>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Category</label>
                      <div className="grid grid-cols-3 gap-2">
                        {(['company', 'team', 'project', 'personal', 'other'] as KnowledgeBaseCategory[]).map(category => (
                          <div
                            key={category}
                            onClick={() => handleCategoryChange(category)}
                            className={`px-3 py-2 rounded-lg cursor-pointer text-center text-sm font-medium transition-colors ${
                              editedItem?.category === category
                                ? 'bg-accent/20 text-accent border border-accent/30'
                                : 'bg-tertiary hover:bg-gray-700'
                            }`}
                          >
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Tags (comma separated)</label>
                      <input
                        type="text"
                        value={editedItem?.tags.join(', ') || ''}
                        onChange={handleTagsChange}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                        placeholder="technical, documentation, official"
                      />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        onClick={handleClosePopup}
                        className="px-4 py-2 bg-tertiary hover:bg-gray-700 transition-colors rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveChanges}
                        className="px-4 py-2 bg-accent hover:bg-accent/90 transition-colors rounded-lg"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-gray-700 rounded-lg p-3">
                        {getFileIcon(selectedItem?.fileType || 'pdf')}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">{selectedItem?.title}</h3>
                        <div className="flex flex-wrap gap-3 mt-2">
                          {!selectedItem?.isLink && (
                            <div className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
                              {selectedItem?.fileType.toUpperCase()}
                            </div>
                          )}
                          <div className="px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-accent/20 to-purple-500/20 text-gray-300 border border-accent/20">
                            {selectedItem?.category.charAt(0).toUpperCase() + selectedItem?.category.slice(1)}
                          </div>
                          <div className="text-gray-400 text-sm flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Added: {formatDate(selectedItem?.dateAdded || '')}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
                      <TruncatedText text={selectedItem?.description || 'No description provided.'} maxLength={200} />
                    </div>

                    {selectedItem?.isLink ? (
                      <div className="bg-tertiary p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                            </svg>
                            <span className="text-gray-300 truncate max-w-[300px]">{selectedItem?.linkUrl}</span>
                          </div>
                        </div>

                        <div className="mt-4 flex justify-between">
                          <button
                            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 transition-colors rounded-lg text-sm font-medium flex items-center"
                            onClick={() => window.open(selectedItem?.linkUrl, '_blank')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Open Link
                          </button>
                          <button className="px-3 py-1.5 bg-accent/20 hover:bg-accent/30 transition-colors rounded-lg text-sm font-medium text-accent flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy URL
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-tertiary p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="text-gray-300">{selectedItem?.fileName}</span>
                          </div>
                          <span className="text-gray-400">{formatFileSize(selectedItem?.fileSize || 0)}</span>
                        </div>

                        {selectedItem?.fileType === 'pdf' && selectedItem?.numpages && (
                          <div className="mt-2 text-sm text-gray-400 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            {selectedItem.numpages} pages
                          </div>
                        )}

                        <div className="mt-4 flex justify-between">
                          <button
                            onClick={() => selectedItem && handleDownloadDocument(selectedItem.id)}
                            disabled={isLoadingPdf}
                            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 transition-colors rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isLoadingPdf ? (
                              <>
                                <div className="w-4 h-4 mr-1 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                                Loading...
                              </>
                            ) : (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Download
                              </>
                            )}
                          </button>
                          {selectedItem?.fileType === 'pdf' && (
                            // <button
                            //   onClick={() => selectedItem && handleViewDocument(selectedItem.id)}
                            //   disabled={isLoadingPdf}
                            //   className="px-3 py-1.5 bg-accent/20 hover:bg-accent/30 transition-colors rounded-lg text-sm font-medium text-accent flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                            // >
                            //   {isLoadingPdf ? (
                            //     <>
                            //       <div className="w-4 h-4 mr-1 border-2 border-t-transparent border-accent rounded-full animate-spin"></div>
                            //       Loading...
                            //     </>
                            //   ) : (
                            //     <>
                            //       <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            //         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            //         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            //       </svg>
                            //       View PDF
                            //     </>
                            //   )}
                            // </button>
                            <></>
                          )}
                        </div>

                        {isPdfUrlError && (
                          <div className="mt-3 px-3 py-2 bg-red-500/20 text-red-300 text-sm rounded-lg">
                            <div className="flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                              </svg>
                              There was an error retrieving the document URL. Please try again.
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedItem?.tags.map(tag => (
                          <span key={tag} className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300">
                            {tag}
                          </span>
                        ))}
                        {selectedItem?.tags.length === 0 && (
                          <div className="text-gray-400">No tags</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Onboarding Data Edit Popup */}
      <AnimatePresence>
        {isEditingOnboarding && editedOnboardingData && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-primary rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar border border-border-color"
            >
              <div className="sticky top-0 z-10 bg-primary p-6 border-b border-border-color flex justify-between items-center backdrop-blur-md bg-opacity-95">
                <h2 className="text-xl font-bold text-white">
                  Edit Onboarding Information
                </h2>
                <button
                  onClick={handleCloseOnboardingEdit}
                  className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Goals</label>
                      <textarea
                        value={editedOnboardingData.goals}
                        onChange={(e) => handleOnboardingInputChange(e, 'goals')}
                        rows={4}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                        placeholder="What are your main goals for using this system?"
                      />
                      <p className="text-xs text-gray-500 mt-1">Enter a brief description of your goals. Keep it concise for better privacy.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Preferences</label>
                      <textarea
                        value={editedOnboardingData.preferences}
                        onChange={(e) => handleOnboardingInputChange(e, 'preferences')}
                        rows={4}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                        placeholder="What are your communication and data preferences?"
                      />
                      <p className="text-xs text-gray-500 mt-1">Consider privacy when entering preferences. Only include necessary details.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Team Information</label>
                      <textarea
                        value={editedOnboardingData.teamInformation || ''}
                        onChange={(e) => handleOnboardingInputChange(e, 'teamInformation')}
                        rows={3}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                        placeholder="Describe your team's structure and processes"
                      />
                      <p className="text-xs text-gray-500 mt-1">Share minimal team details for privacy. Use general descriptions when possible.</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Business Information</label>
                      <textarea
                        value={editedOnboardingData.businessInformation || ''}
                        onChange={(e) => handleOnboardingInputChange(e, 'businessInformation')}
                        rows={3}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                        placeholder="Describe your business details and operations"
                      />
                      <p className="text-xs text-gray-500 mt-1">Keep business information concise and focused on essentials only.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Communication Style</label>
                      <input
                        type="text"
                        value={editedOnboardingData.communicationStyle || ''}
                        onChange={(e) => handleOnboardingInputChange(e, 'communicationStyle')}
                        className="w-full px-4 py-2 bg-tertiary rounded-lg border border-border-color focus:border-accent outline-none"
                        placeholder="Describe your preferred communication style"
                      />
                    </div>

                    {/* External Resources / Links */}
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">External Resources</label>

                      <div className="mb-3 p-4 bg-tertiary rounded-lg border border-border-color">
                        <div className="text-center py-4">
                          <p className="text-gray-300 font-medium">Coming Soon</p>
                          <p className="text-gray-400 text-sm mt-1">External resource management will be available in a future update.</p>
                        </div>
                      </div>
                    </div> */}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 mt-6">
                  <button
                    onClick={handleCloseOnboardingEdit}
                    className="px-4 py-2 bg-tertiary hover:bg-gray-700 transition-colors rounded-lg"
                    disabled={isSavingOnboarding}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveOnboardingChanges}
                    className="px-4 py-2 bg-accent hover:bg-accent/90 transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSavingOnboarding}
                  >
                    {isSavingOnboarding ? (
                      <div className="flex items-center">
                        <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </div>
                    ) : "Save Changes"}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast.visible && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg ${
              toast.type === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}
          >
            <div className="flex items-center">
              {toast.type === 'success' ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span>{toast.message}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Dialog */}
      <AnimatePresence>
        {isConfirmingDelete && itemToDelete && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-primary rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-border-color"
            >
              <div className="p-6 border-b border-border-color">
                <h2 className="text-xl font-bold text-white">Confirm Delete</h2>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <p className="text-gray-300">
                    Are you sure you want to delete <span className="font-semibold text-white">"{itemToDelete.title}"</span>?
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    This action cannot be undone.
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    onClick={handleCancelDelete}
                    className="px-4 py-2 bg-tertiary hover:bg-gray-700 transition-colors rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 transition-colors rounded-lg text-white"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KnowledgeBase;
